import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Trash2, Download, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import PayPalCheckout from "@/components/PayPalCheckout";
import { BuyerDetails } from "@/components/WorkshopBookDialog";
import { trackBeginCheckout } from "@/lib/analytics";

const KIND_LABEL = { resource: "Digital guide", workshop: "Live workshop", bundle: "Workshop bundle", service: "Service" };

export default function CartDrawer() {
  const { items, remove, clear, total, count, open, setOpen } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState("basket");
  const [form, setForm] = useState({ name: "", email: "", marketing_opt_in: false });
  const [result, setResult] = useState(null);

  useEffect(() => { setForm((f) => ({ name: f.name || user?.name || "", email: f.email || user?.email || "" })); }, [user]);
  useEffect(() => { if (!open && step !== "done") setStep("basket"); }, [open, step]);

  const onSuccess = (data) => { setResult(data); setStep("done"); clear(); };
  const close = () => { setOpen(false); setStep("basket"); setResult(null); };

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="font-display text-xl flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-teal-600" /> {step === "done" ? "Order complete" : "Your basket"}</SheetTitle>
        <SheetDescription>{step === "done" ? "Thank you for your purchase." : count === 0 ? "Nothing here yet." : `${count} item${count > 1 ? "s" : ""} · $${total} AUD`}</SheetDescription>

        {step === "done" && result && (
          <div className="flex-1 overflow-y-auto py-4 text-center" data-testid="cart-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><ShieldCheck className="w-7 h-7 text-emerald-600" /></div>
            <p className="text-slate-700">Payment received. A receipt{result.payer_email ? ` has been sent to ${result.payer_email}` : " is on its way"}.</p>
            {result.downloads?.length > 0 && (
              <div className="mt-5 space-y-2 text-left">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your downloads (valid 30 days)</p>
                {result.downloads.map((d) => (
                  <Button key={d.item_id} asChild className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid={`cart-download-${d.item_id}`}>
                    <a href={d.url} target="_blank" rel="noreferrer"><Download className="w-4 h-4" /> {d.title}</a>
                  </Button>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-5">Workshop seats and recordings appear in <Link to="/account" className="text-teal-700 underline" onClick={close}>My Account</Link> when you sign in with the same email.</p>
            <Button variant="ghost" className="mt-3" onClick={close} data-testid="cart-done-close">Close</Button>
          </div>
        )}

        {step === "basket" && (
          <div className="flex-1 flex flex-col min-h-0">
            {count === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-500" data-testid="cart-empty">
                <ShoppingBag className="w-10 h-10 text-slate-300 mb-3" />
                <p>Your basket is empty.</p>
                <Button asChild variant="outline" className="mt-4 rounded-full"><Link to="/resources" onClick={close}>Browse resources</Link></Button>
              </div>
            ) : (
              <>
                <ul className="flex-1 overflow-y-auto divide-y divide-slate-100 -mx-1 px-1" data-testid="cart-items">
                  {items.map((it) => (
                    <li key={it.key} className="py-3 flex items-start gap-3" data-testid={`cart-item-${it.item_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{it.name}</p>
                        <p className="text-xs text-slate-500">{KIND_LABEL[it.kind] || "Item"}{it.detail ? ` · ${it.detail}` : ""}</p>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">${it.price}</span>
                      <button onClick={() => remove(it.key)} className="text-slate-400 hover:text-red-600 transition-colors" aria-label="Remove" data-testid={`cart-remove-${it.item_id}`}><Trash2 className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-slate-200 pt-4 mt-2">
                  <div className="flex justify-between text-base font-semibold text-slate-900 mb-4"><span>Total</span><span data-testid="cart-total">${total} AUD</span></div>
                  <Button className="w-full bg-teal-600 hover:bg-teal-700 rounded-full" onClick={() => { trackBeginCheckout(items, total); setStep("details"); }} data-testid="cart-checkout-btn">Checkout</Button>
                  <button onClick={clear} className="w-full text-xs text-slate-500 hover:text-slate-800 mt-3" data-testid="cart-clear">Clear basket</button>
                </div>
              </>
            )}
          </div>
        )}

        {(step === "details" || step === "pay") && (
          <div className="flex-1 overflow-y-auto">
            <button onClick={() => setStep("basket")} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-4" data-testid="cart-back"><ArrowLeft className="w-3 h-3" /> Back to basket</button>
            {step === "details" && <BuyerDetails form={form} setForm={setForm} price={total} testPrefix="cart" onNext={() => setStep("pay")} />}
            {step === "pay" && (
              <PayPalCheckout itemName="your basket" orderMeta={{ items: items.map(({ item_id, workshop_ids }) => ({ item_id, workshop_ids })), name: form.name, email: form.email, marketing_opt_in: !!form.marketing_opt_in }}
                onSuccess={onSuccess} onClose={close} />
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
